import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerTopHeader from '../components/ManagerTopHeader';

import { API_BASE } from '../../config/runtime';

const getStoredUser = () => {
  try {
    const rawUser = localStorage.getItem('user');
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

const normalizeStatus = (status) => {
  if (!status) return '';
  const s = status.trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatDuration = (totalMinutes) => {
  if (!totalMinutes) return 'N/A';
  if (totalMinutes < 60) return `${totalMinutes} mins`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (mins === 0) return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${mins} mins`;
};

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  const [hour, minute] = timeStr.split(':');
  const temp = new Date();
  temp.setHours(Number(hour || 0), Number(minute || 0), 0, 0);
  return temp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatMoney = (value) => Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const getBookingDurationMinutes = (booking) => {
  if (booking?.services && booking.services.length > 0) {
    const total = booking.services.reduce((sum, svc) => sum + Number(svc.duration_minutes || 0), 0);
    return total > 0 ? total : Number(booking?.duration_minutes || 0);
  }
  return Number(booking?.duration_minutes || 0);
};

const STATUS_META = {
  Pending:   { badge: 'bg-amber-100 text-amber-800 border-amber-200',   dot: 'bg-amber-400' },
  Approved:  { badge: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500' },
  Completed: { badge: 'bg-blue-100 text-blue-800 border-blue-200',      dot: 'bg-blue-500' },
  Declined:  { badge: 'bg-red-100 text-red-800 border-red-200',         dot: 'bg-red-500' },
  Cancelled: { badge: 'bg-gray-100 text-gray-600 border-gray-200',      dot: 'bg-gray-400' },
};

const EDITABLE_GUEST_STATUSES = ['Pending', 'Approved'];
const BOOKING_TIME_OPTIONS = [
  ['09:00:00', '9:00 AM'],
  ['10:00:00', '10:00 AM'],
  ['11:00:00', '11:00 AM'],
  ['12:00:00', '12:00 PM'],
  ['13:00:00', '1:00 PM'],
  ['14:00:00', '2:00 PM'],
  ['15:00:00', '3:00 PM'],
  ['16:00:00', '4:00 PM'],
  ['17:00:00', '5:00 PM'],
  ['18:00:00', '6:00 PM'],
  ['19:00:00', '7:00 PM'],
  ['20:00:00', '8:00 PM'],
];

const getTodayDate = () => new Date().toISOString().split('T')[0];

const normalizeContact = (value) => (value || '').replace(/\D/g, '').slice(0, 11);

const normalizeTimeValue = (value) => {
  if (!value) return '';
  return value.length === 5 ? `${value}:00` : value;
};

const buildSelectedServices = (targetBooking, serviceCatalog) => {
  if (!targetBooking) return [];
  const catalog = Array.isArray(serviceCatalog) ? serviceCatalog : [];

  if (Array.isArray(targetBooking.services) && targetBooking.services.length > 0) {
    return targetBooking.services.map((svc) => {
      const match = catalog.find((item) => String(item.id) === String(svc.service_id));
      if (match) return match;
      return {
        id: Number(svc.service_id || svc.id),
        name: svc.service_name || svc.name || 'Service',
        duration_minutes: Number(svc.duration_minutes || 0),
        price: Number(svc.price || 0),
      };
    });
  }

  if (targetBooking.service_id) {
    const match = catalog.find((item) => String(item.id) === String(targetBooking.service_id));
    if (match) return [match];
    return [{
      id: Number(targetBooking.service_id),
      name: targetBooking.service_name || 'Service',
      duration_minutes: Number(targetBooking.duration_minutes || 0),
      price: Number(targetBooking.service_price || 0),
    }];
  }

  return [];
};

// ── Toast Notification ────────────────────────────────────────────────────────
const TOAST_DURATION = 4000;

const ToastIcon = ({ type }) => {
  if (type === 'success') return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
    </svg>
  );
  if (type === 'error') return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

const TOAST_STYLES = {
  success: {
    wrapper: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    icon: 'text-emerald-600 bg-emerald-100',
    bar: 'bg-emerald-400',
    close: 'text-emerald-500 hover:text-emerald-700',
  },
  error: {
    wrapper: 'bg-red-50 border-red-200 text-red-800',
    icon: 'text-red-600 bg-red-100',
    bar: 'bg-red-400',
    close: 'text-red-500 hover:text-red-700',
  },
  info: {
    wrapper: 'bg-blue-50 border-blue-200 text-blue-800',
    icon: 'text-blue-600 bg-blue-100',
    bar: 'bg-blue-400',
    close: 'text-blue-500 hover:text-blue-700',
  },
};

const Toast = ({ id, message, type = 'info', onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const styles = TOAST_STYLES[type] || TOAST_STYLES.info;

  useEffect(() => {
    const enterTimer = setTimeout(() => setVisible(true), 10);
    const leaveTimer = setTimeout(() => handleDismiss(), TOAST_DURATION);
    return () => { clearTimeout(enterTimer); clearTimeout(leaveTimer); };
  }, []);

  const handleDismiss = () => {
    setLeaving(true);
    setTimeout(() => onDismiss(id), 300);
  };

  return (
    <div
      className={`
        relative w-full max-w-sm rounded-2xl border shadow-lg overflow-hidden
        transition-all duration-300 ease-in-out
        ${styles.wrapper}
        ${visible && !leaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
      style={{ willChange: 'transform, opacity' }}
    >
      <div className="flex items-start gap-3 p-4 pr-10">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${styles.icon}`}>
          <ToastIcon type={type} />
        </div>
        <p className="text-sm font-semibold leading-snug flex-1 whitespace-pre-line">{message}</p>
      </div>

      {/* Close button */}
      <button
        onClick={handleDismiss}
        className={`absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-full transition ${styles.close}`}
        aria-label="Dismiss"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Progress bar */}
      <div className={`absolute bottom-0 left-0 h-0.5 ${styles.bar}`}
        style={{
          width: '100%',
          animation: `toast-shrink ${TOAST_DURATION}ms linear forwards`,
        }}
      />

      <style>{`
        @keyframes toast-shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
  );
};

const ToastContainer = ({ toasts, onDismiss }) => (
  <div className="fixed top-4 right-4 z-[200] flex flex-col gap-2.5 items-end pointer-events-none">
    {toasts.map((t) => (
      <div key={t.id} className="pointer-events-auto w-full max-w-sm">
        <Toast id={t.id} message={t.message} type={t.type} onDismiss={onDismiss} />
      </div>
    ))}
  </div>
);

// ── Edit Guest Booking Modal ──────────────────────────────────────────────────
const EditGuestBookingModal = ({ booking, services, saving, onClose, onSave, showToast }) => {
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_contact: '',
    guest_email: '',
    appointment_date: '',
    appointment_time: '',
    number_of_clients: 1,
  });
  const [selectedServices, setSelectedServices] = useState([]);

  useEffect(() => {
    if (!booking) return;
    setFormData({
      guest_name: booking.guest_name || booking.customer_name || '',
      guest_contact: normalizeContact(booking.guest_contact || booking.customer_contact || ''),
      guest_email: booking.guest_email || booking.customer_email || '',
      appointment_date: booking.appointment_date || '',
      appointment_time: normalizeTimeValue(booking.appointment_time || ''),
      number_of_clients: Math.max(1, Number(booking.number_of_clients || 1)),
    });
    setSelectedServices(buildSelectedServices(booking, services));
  }, [booking, services]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const toggleService = (service) => {
    setSelectedServices((prev) => {
      const exists = prev.some((item) => String(item.id) === String(service.id));
      return exists
        ? prev.filter((item) => String(item.id) !== String(service.id))
        : [...prev, service];
    });
  };

  const totalDuration = selectedServices.reduce((sum, svc) => sum + Number(svc.duration_minutes || 0), 0);
  const totalAmount = selectedServices.reduce((sum, svc) => sum + Number(svc.price || 0), 0) * Number(formData.number_of_clients || 1);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.guest_name.trim()) { showToast('Please enter the guest name', 'error'); return; }
    if (formData.guest_contact.length !== 11) { showToast('Contact number must be exactly 11 digits.', 'error'); return; }
    if (!formData.guest_email.trim()) { showToast('Please enter the guest email address', 'error'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guest_email.trim())) { showToast('Please enter a valid email address', 'error'); return; }
    if (!formData.appointment_date || !formData.appointment_time) { showToast('Please select the appointment date and time', 'error'); return; }
    if (selectedServices.length === 0) { showToast('Please select at least one service', 'error'); return; }
    onSave({ ...formData, services: selectedServices });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[130] p-0 sm:p-4" onClick={onClose}>
      <div
        className="w-full sm:max-w-3xl bg-white rounded-t-[2rem] sm:rounded-3xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        <div className="px-5 sm:px-6 pt-3 sm:pt-6 pb-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-bold text-[#b37b65] uppercase tracking-[0.18em]">Guest Booking</p>
            <h3 className="text-lg sm:text-xl font-black text-gray-900">Edit Booking</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition flex items-center justify-center"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form id="edit-guest-booking-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 sm:py-5 space-y-5">
          <div className="bg-[#faf5ef] border border-[#eadfd5] rounded-2xl p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b5e3c] mb-2">Branch</p>
            <p className="text-sm font-bold text-gray-900">{booking.branch_name || 'Assigned Branch'}</p>
            <p className="text-sm text-gray-600 mt-1">{booking.branch_address || '-'}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Guest Name</label>
              <input
                type="text"
                value={formData.guest_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, guest_name: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Contact Number</label>
              <input
                type="tel"
                value={formData.guest_contact}
                onChange={(e) => setFormData((prev) => ({ ...prev, guest_contact: normalizeContact(e.target.value) }))}
                inputMode="numeric"
                pattern="[0-9]{11}"
                maxLength={11}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Email Address</label>
            <input
              type="email"
              value={formData.guest_email}
              onChange={(e) => setFormData((prev) => ({ ...prev, guest_email: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Services</label>
            <div className="border border-gray-200 rounded-2xl overflow-hidden max-h-60 overflow-y-auto">
              {services.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No services are available for this branch.</div>
              ) : services.map((service) => {
                const isSelected = selectedServices.some((item) => String(item.id) === String(service.id));
                return (
                  <label
                    key={service.id}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${isSelected ? 'bg-[#faf5ef]' : 'bg-white'}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleService(service)}
                      className="h-4 w-4 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{service.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{formatDuration(Number(service.duration_minutes || 0))}</p>
                    </div>
                    <span className="text-xs font-bold text-[#6f4a42] flex-shrink-0">₱{Number(service.price || 0).toLocaleString()}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1.5">Selected: {selectedServices.length}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">No. of Clients</label>
              <input
                type="number"
                min="1"
                value={formData.number_of_clients}
                onChange={(e) => setFormData((prev) => ({ ...prev, number_of_clients: Math.max(1, Number(e.target.value) || 1) }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Date</label>
              <input
                type="date"
                value={formData.appointment_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, appointment_date: e.target.value }))}
                min={getTodayDate()}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Time</label>
              <select
                value={formData.appointment_time}
                onChange={(e) => setFormData((prev) => ({ ...prev, appointment_time: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm appearance-none"
                required
              >
                <option value="">Select a time</option>
                {BOOKING_TIME_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Total Duration</span>
              <span>{totalDuration > 0 ? formatDuration(totalDuration) : '-'}</span>
            </div>
            <div className="flex justify-between text-base font-black text-gray-900 mt-2">
              <span>Total Amount</span>
              <span className="text-[#6f4a42]">₱{Number(totalAmount || 0).toLocaleString()}</span>
            </div>
          </div>
        </form>

        <div className="px-5 sm:px-6 py-4 border-t border-gray-100 bg-white flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-guest-booking-form"
            disabled={saving}
            className="flex-1 px-4 py-3 bg-[#1c1310] text-white rounded-xl font-bold text-sm hover:bg-[#2e1f18] transition disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Receipt Modal ────────────────────────────────────────────────────────────
const ReceiptModal = ({ booking, onClose }) => {
  const services = booking.services || [];
  const numClients = Number(booking.number_of_clients || 1);
  const clientName = booking.customer_name || booking.guest_name || 'Client';
  const receiptDate = new Date().toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  const singleServicePrice = services.length > 0 ? services[0].price : booking.service_price || 0;
  const receiptId = booking.receipt_code || String(booking.booking_id).padStart(7, '0');
  const totalPriceNumber = Number(booking.total_price || (Number(singleServicePrice) * numClients) || 0);
  const totalPrice = formatMoney(totalPriceNumber);
  const paymentStatus = String(booking.payment_status || '').toLowerCase();
  const paidAmountNumber = Number(
    booking.amount_paid
    || (paymentStatus === 'paid' ? (totalPriceNumber * 0.5) : 0)
  );
  const paidPercentage = totalPriceNumber > 0
    ? Math.round((paidAmountNumber / totalPriceNumber) * 100)
    : 0;
  const totalDurationMinutes = services.length > 0
    ? services.reduce((sum, svc) => sum + Number(svc.duration_minutes || 0), 0)
    : Number(booking.duration_minutes || 0);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-[120] p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-sm mx-auto bg-[#f6f5f2] text-gray-800 font-mono shadow-2xl border border-gray-300 rounded-t-3xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 sm:px-6 py-6">
          <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-5 sm:hidden" />

          <div className="text-center">
            <div className="text-base sm:text-lg font-bold tracking-[0.3em]">HYGEA WELLNESS</div>
            <div className="text-[11px] text-gray-600">Branch: {booking.branch_name || 'Main Branch'}</div>
            <div className="text-[11px] text-gray-600">Address: {booking.branch_address || '-'}</div>
            <div className="text-[11px] text-gray-600">Receipt #{receiptId}</div>
            <div className="text-[11px] text-gray-600">{receiptDate}</div>
          </div>

          <div className="text-center text-xs tracking-[0.3em] text-gray-400 my-4">* * * * * * * * * *</div>
          <div className="text-center text-sm font-bold tracking-[0.3em]">APPOINTMENT RECEIPT</div>
          <div className="text-center text-xs tracking-[0.3em] text-gray-400 my-4">* * * * * * * * * *</div>

          <div className="text-[11px] text-gray-500 space-y-0.5">
            <div>Client: {clientName}</div>
            <div>No. of Person/s: {numClients}</div>
            <div>Appointment: {formatDate(booking.appointment_date)} {formatTime(booking.appointment_time)}</div>
          </div>

          <div className="my-3 border-t border-dashed border-gray-300" />

          <div className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-gray-600">
            <span>Description</span><span>Amount</span>
          </div>
          <div className="my-2 border-t border-dashed border-gray-300" />

          <div className="space-y-3">
            {services.length > 0 ? services.map((svc, idx) => (
              <div key={idx}>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{svc.service_name}</span>
                  <span className="font-semibold">₱{formatMoney(Number(svc.price || 0) * numClients)}</span>
                </div>
                <div className="text-[10px] text-gray-500">Duration: {formatDuration(Number(svc.duration_minutes || 0))}</div>
              </div>
            )) : (
              <div>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{booking.service_name || 'Service'}</span>
                  <span className="font-semibold">₱{formatMoney(Number(singleServicePrice || 0) * numClients)}</span>
                </div>
                <div className="text-[10px] text-gray-500">Duration: {formatDuration(Number(booking.duration_minutes || 0))}</div>
              </div>
            )}
          </div>

          <div className="my-4 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-sm font-semibold text-gray-600">
            <span>Total Duration</span><span>{formatDuration(totalDurationMinutes)}</span>
          </div>
          <div className="my-3 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span><span>₱{totalPrice}</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-2">
            Paid: ₱{formatMoney(paidAmountNumber)}{paymentStatus === 'paid' ? ` (${paidPercentage}%)` : ''}
          </div>
          <div className="text-center text-xs tracking-[0.3em] text-gray-400 my-4">* * * * * * * * * *</div>
          <div className="text-center text-sm font-bold tracking-[0.4em]">THANK YOU!</div>
          <div className="text-center text-[10px] text-gray-500 mt-3">Hygea Wellness · All Rights Reserved © 2024</div>

          <button
            onClick={onClose}
            className="mt-6 w-full py-3 bg-[#1c1310] hover:bg-[#2e1f18] text-white font-bold uppercase text-sm tracking-wide rounded-xl transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ tab, label, value, isActive, dotColor, onClick }) => (
  <button
    onClick={onClick}
    className={`
      text-left p-4 rounded-2xl border transition-all duration-150
      ${isActive
        ? 'bg-[#1c1310] border-[#1c1310] text-white shadow-lg shadow-[#1c1310]/20'
        : 'bg-white border-gray-100 text-gray-700 hover:border-gray-200 hover:shadow-sm shadow-sm'
      }
    `}
  >
    <div className="flex items-center gap-2 mb-2">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-white/60' : dotColor}`} />
      <p className={`text-[10px] font-bold uppercase tracking-[0.15em] ${isActive ? 'text-white/60' : 'text-gray-400'}`}>
        {label}
      </p>
    </div>
    <p className={`text-3xl font-black leading-none ${isActive ? 'text-white' : 'text-gray-900'}`}>{value}</p>
  </button>
);

// ── Detail Pill ───────────────────────────────────────────────────────────────
const DetailPill = ({ label, value }) => (
  <div className="bg-gray-50 rounded-xl p-3">
    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-sm font-semibold text-gray-800 leading-snug">{value}</p>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const ManagerBookingRequest = () => {
  const location = useLocation();
  const user = getStoredUser();
  const branchId = user?.branch_id;
  const highlightBookingId = new URLSearchParams(location.search).get('highlight') || '';

  const [bookings, setBookings] = useState([]);
  const [statistics, setStatistics] = useState({ total: 0, pending: 0, approved: 0, declined: 0, cancelled: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [reviewingId, setReviewingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedReceiptBooking, setSelectedReceiptBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toasts, setToasts] = useState([]);
  const [activeHighlightId, setActiveHighlightId] = useState('');

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const fetchBookings = async () => {
    if (!branchId) { setLoading(false); return; }
    setLoading(true);
    try {
      const statusParam = activeTab !== 'all' ? `&status=${activeTab}` : '';
      const response = await fetch(`${API_BASE}/get_branch_bookings.php?branch_id=${branchId}${statusParam}`);
      const result = await response.json();
      if (result.success) {
        setBookings(result.bookings || []);
        setStatistics(result.statistics || { total: 0, pending: 0, approved: 0, declined: 0, cancelled: 0, completed: 0 });
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_services.php`);
      const result = await response.json();
      if (result.success) {
        const branchServices = (result.services || []).filter((service) => {
          const allowedBranchIds = Array.isArray(service.branch_ids) ? service.branch_ids : [];
          return allowedBranchIds.length === 0 || allowedBranchIds.some((id) => Number(id) === Number(branchId));
        });
        setServices(branchServices);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  useEffect(() => { fetchBookings(); }, [branchId, activeTab]);
  useEffect(() => { if (branchId) fetchServices(); }, [branchId]);
  useEffect(() => {
    if (!highlightBookingId) return;
    setActiveTab('all');
    setActiveHighlightId(highlightBookingId);
  }, [highlightBookingId]);

  const handleStatusUpdate = async (bookingId, newStatus, comment = null, action = null) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/update_booking_status.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          status: newStatus,
          comment,
          action,
          manager_user_id: user?.user_id || null,
        }),
      });
      const result = await response.json();
      if (result.success) {
        if (action !== 'suggest_change') {
          const currentBooking = bookings.find((b) => b.booking_id === bookingId);
          const previousStatus = normalizeStatus(currentBooking?.status);
          setBookings((prev) => prev.filter((b) => b.booking_id !== bookingId));
          setStatistics((prev) => {
            const fromKey = previousStatus.toLowerCase();
            const toKey = newStatus.toLowerCase();
            const next = { ...prev };
            if (fromKey && typeof next[fromKey] === 'number') next[fromKey] = Math.max(0, next[fromKey] - 1);
            if (toKey) next[toKey] = (next[toKey] || 0) + 1;
            next.total = prev.total;
            return next;
          });
        } else {
          fetchBookings();
        }
        setReviewingId(null);

        let message = `Booking ${newStatus.toLowerCase()} successfully!`;
        let toastType = 'success';
        if (result.notification_sent) {
          message += '\n✓ Notification sent to client.';
        } else if (newStatus !== 'Pending') {
          message += '\nNote: Client notification was not sent (guest booking).';
          toastType = 'info';
        }
        showToast(message, toastType);
      } else {
        showToast(result.message || 'Failed to update booking status', 'error');
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      showToast('An error occurred while updating the booking', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGuestBookingUpdate = async (formData) => {
    if (!editingBooking || !branchId) return;

    const selectedServices = Array.isArray(formData?.services) ? formData.services : [];
    if (selectedServices.length === 0) {
      showToast('Please select at least one service', 'error');
      return;
    }

    setEditLoading(true);
    try {
      const response = await fetch(`${API_BASE}/update_guest_booking_by_manager.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: editingBooking.booking_id,
          branch_id: branchId,
          guest_name: formData.guest_name,
          guest_contact: formData.guest_contact,
          guest_email: formData.guest_email,
          appointment_date: formData.appointment_date,
          appointment_time: formData.appointment_time,
          number_of_clients: formData.number_of_clients,
          services: selectedServices.map((service) => ({ service_id: service.id })),
        }),
      });
      const result = await response.json();
      if (result.success) {
        setEditingBooking(null);
        fetchBookings();
        showToast('Guest booking updated successfully.', 'success');
      } else {
        showToast(result.message || 'Failed to update guest booking', 'error');
      }
    } catch (error) {
      console.error('Error updating guest booking:', error);
      showToast('An error occurred while updating the guest booking', 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;
    const serviceNames = Array.isArray(booking.services)
      ? booking.services.map((svc) => svc.service_name).join(' ') : '';
    const haystack = [
      booking.customer_name, booking.customer_contact, booking.booking_id,
      booking.service_name, serviceNames, booking.branch_name,
      booking.appointment_date, booking.appointment_time,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(query);
  });

  useEffect(() => {
    if (!activeHighlightId || loading) return undefined;
    const target = document.getElementById(`manager-booking-${activeHighlightId}`);
    if (!target) return undefined;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timerId = window.setTimeout(() => setActiveHighlightId(''), 4000);
    return () => window.clearTimeout(timerId);
  }, [activeHighlightId, loading, bookings]);

  if (!branchId) {
    return (
      <div className="flex min-h-screen hygea-app-bg">
        <ManagerSidebar />
        <div className="flex-1 flex flex-col">
          <ManagerTopHeader pageTitle="Booking Requests" />
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center">
              <p className="text-gray-500 font-semibold">No branch assigned to your account.</p>
              <p className="text-sm text-gray-400 mt-1">Please contact the system administrator.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const STAT_ITEMS = [
    { tab: 'all',       label: 'Total',     value: statistics.total,     dot: 'bg-[#984e5c]' },
    { tab: 'Pending',   label: 'Pending',   value: statistics.pending,   dot: 'bg-amber-400' },
    { tab: 'Approved',  label: 'Approved',  value: statistics.approved,  dot: 'bg-emerald-500' },
    { tab: 'Declined',  label: 'Declined',  value: statistics.declined,  dot: 'bg-red-500' },
    { tab: 'Cancelled', label: 'Cancelled', value: statistics.cancelled, dot: 'bg-gray-400' },
    { tab: 'Completed', label: 'Completed', value: statistics.completed, dot: 'bg-blue-500' },
  ];

  return (
    <div className="flex min-h-screen hygea-app-bg">
      <ManagerSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <ManagerTopHeader pageTitle="Booking Requests" />

        <main className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">

            {/* ── Page header ── */}
            <div className="mb-5 sm:mb-6">
              <p className="text-[11px] font-bold text-[#b37b65] uppercase tracking-[0.2em] mb-0.5">Management</p>
              <h1 className="text-xl sm:text-2xl font-black text-gray-900 leading-tight">Booking Requests</h1>
            </div>

            {/* ── Stat Cards ── */}
            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              {STAT_ITEMS.map(({ tab, label, value, dot }) => (
                <StatCard
                  key={tab}
                  tab={tab}
                  label={label}
                  value={value}
                  isActive={activeTab === tab}
                  dotColor={dot}
                  onClick={() => setActiveTab(tab)}
                />
              ))}
            </div>

            {/* ── Filters row ── */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
              <div className="flex gap-1.5 overflow-x-auto pb-0.5 flex-1 scrollbar-hide">
                {['all', 'Pending', 'Approved', 'Completed', 'Declined', 'Cancelled'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                      activeTab === tab
                        ? 'bg-[#1c1310] text-white shadow'
                        : 'bg-white text-gray-500 hover:text-gray-800 border border-gray-100'
                    }`}
                  >
                    {tab === 'all' ? 'All' : tab}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-100 shadow-sm sm:w-64 flex-shrink-0">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search bookings…"
                  className="w-full bg-transparent text-sm outline-none text-gray-700 placeholder:text-gray-400"
                />
              </div>
            </div>

            {/* ── Content ── */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
                <svg className="w-8 h-8 animate-spin text-[#984e5c]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <p className="text-sm font-medium">Loading bookings…</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
                <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-gray-700 font-bold">No bookings found</p>
                <p className="text-sm text-gray-400 mt-1">
                  {searchTerm.trim() ? 'No bookings match your search' : activeTab !== 'all' ? `No ${activeTab.toLowerCase()} bookings` : 'No bookings for your branch yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((booking) => {
                  const status = normalizeStatus(booking.status);
                  const meta = STATUS_META[status] || STATUS_META.Cancelled;
                  const canComplete = status === 'Approved';
                  const canViewReceipt = status === 'Approved' || status === 'Completed';
                  const canEditGuestBooking = booking.customer_type === 'guest' && EDITABLE_GUEST_STATUSES.includes(status);
                  const isPending = status === 'Pending';
                  const isReviewing = reviewingId === booking.booking_id;

                  return (
                    <div
                      key={booking.booking_id}
                      id={`manager-booking-${booking.booking_id}`}
                      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200"
                      style={String(booking.booking_id) === activeHighlightId
                        ? {
                            borderColor: '#c8896e',
                            boxShadow: '0 0 0 3px rgba(200,137,110,0.18), 0 12px 30px rgba(28,19,16,0.10)',
                            background: 'linear-gradient(180deg, #fffdfb 0%, #ffffff 100%)',
                          }
                        : undefined}
                    >

                      {status === 'Completed' && (
                        <div className="bg-blue-50 border-b border-blue-100 px-4 sm:px-6 py-2 flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Completed</span>
                        </div>
                      )}

                      <div className="p-4 sm:p-6">
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-[#f7ece8] flex items-center justify-center flex-shrink-0">
                              <span className="text-[#984e5c] font-black text-base sm:text-lg">
                                {booking.customer_name ? booking.customer_name.charAt(0).toUpperCase() : '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-base sm:text-lg font-black text-gray-900 truncate">
                                {booking.customer_name || 'Guest User'}
                              </h3>
                              <p className="text-xs text-gray-400 truncate">
                                {booking.customer_contact}
                                {booking.customer_contact && ' · '}
                                {booking.customer_type === 'guest' ? 'Guest' : 'Registered'}
                              </p>
                            </div>
                          </div>

                          <div className="text-right flex-shrink-0">
                            <span className={`inline-block border px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide mb-1.5 ${meta.badge}`}>
                              {status}
                            </span>
                            <p className="text-xl sm:text-2xl font-black text-[#6f4a42]">
                              ₱{Number(booking.total_price || 0).toLocaleString()}
                            </p>
                            <p className={`text-[11px] font-semibold mt-0.5 ${String(booking.payment_status || '').toLowerCase() === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`}>
                              {String(booking.payment_status || '').toLowerCase() === 'paid'
                                ? `Paid (50%): ₱${formatMoney(booking.amount_paid || 0)}`
                                : `Payment: ${booking.payment_status || 'unpaid'}`}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 mb-4">
                          <DetailPill
                            label="Service/s"
                            value={
                              booking.services?.length > 0
                                ? booking.services.map((s) => s.service_name).join(', ')
                                : booking.service_name || 'N/A'
                            }
                          />
                          <DetailPill label="Duration" value={formatDuration(getBookingDurationMinutes(booking))} />
                          <DetailPill
                            label="Clients"
                            value={`${Number(booking.number_of_clients || 1)} ${Number(booking.number_of_clients || 1) === 1 ? 'Client' : 'Clients'}`}
                          />
                          <DetailPill
                            label="Date & Time"
                            value={`${formatDate(booking.appointment_date)} · ${formatTime(booking.appointment_time)}`}
                          />
                          <DetailPill
                            label="Payment"
                            value={String(booking.payment_status || '').toLowerCase() === 'paid'
                              ? `Paid (50%) ₱${formatMoney(booking.amount_paid || 0)}`
                              : (booking.payment_status || 'Unpaid')}
                          />
                        </div>

                        {/* ── Footer: ID + Actions ── */}
                        {booking.customer_suggestion && booking.customer_suggestion.trim() !== '' && (
                          <div className="mb-4 rounded-xl border border-[#eadfd5] bg-[#fffaf5] px-4 py-3">
                            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b37b65] mb-1">
                              Customer Suggestion
                            </p>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {booking.customer_suggestion}
                            </p>
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-gray-50">
                          <p className="text-xs text-gray-400 font-mono">Booking #{booking.booking_id}</p>

                          <div className="flex flex-wrap gap-2 sm:justify-end">
                            {canEditGuestBooking && (
                              <button
                                onClick={() => setEditingBooking(booking)}
                                className="flex-1 sm:flex-none px-4 py-2 rounded-xl font-bold text-xs bg-[#faf5ef] text-[#8b5e3c] hover:bg-[#f4e8dc] transition border border-[#eadfd5]"
                              >
                                Edit Guest Booking
                              </button>
                            )}
                            {canViewReceipt && (
                              <button
                                onClick={() => setSelectedReceiptBooking(booking)}
                                className="flex-1 sm:flex-none px-4 py-2 rounded-xl font-bold text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 transition border border-blue-100"
                              >
                                View Receipt
                              </button>
                            )}
                            {canComplete && (
                              <button
                                onClick={() => handleStatusUpdate(booking.booking_id, 'Completed')}
                                disabled={actionLoading}
                                className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs hover:bg-blue-700 transition disabled:opacity-50"
                              >
                                {actionLoading ? 'Saving…' : 'Mark Complete'}
                              </button>
                            )}
                            {isPending && !isReviewing && (
                              <>
                                <button
                                  onClick={() => handleStatusUpdate(booking.booking_id, 'Approved')}
                                  disabled={actionLoading}
                                  className="flex-1 sm:flex-none px-4 py-2 bg-emerald-500 text-white rounded-xl font-bold text-xs hover:bg-emerald-600 transition disabled:opacity-50"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => setReviewingId(booking.booking_id)}
                                  disabled={actionLoading}
                                  className="flex-1 sm:flex-none px-4 py-2 bg-amber-500 text-white rounded-xl font-bold text-xs hover:bg-amber-600 transition disabled:opacity-50"
                                >
                                  Suggest
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(booking.booking_id, 'Declined')}
                                  disabled={actionLoading}
                                  className="flex-1 sm:flex-none px-4 py-2 bg-gray-50 text-gray-600 rounded-xl font-bold text-xs hover:bg-red-50 hover:text-red-600 transition disabled:opacity-50 border border-gray-100"
                                >
                                  Decline
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {isReviewing && (
                          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:p-5">
                            <p className="text-sm font-bold text-amber-800 mb-3">Suggest Modifications</p>
                            <textarea
                              id={`comment-${booking.booking_id}`}
                              rows={3}
                              placeholder="e.g., Please reschedule — our therapist is unavailable at this time…"
                              className="w-full px-3 py-2.5 rounded-xl border border-amber-200 text-sm outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 resize-none bg-white"
                            />
                            <div className="flex justify-end gap-2 mt-3">
                              <button
                                onClick={() => setReviewingId(null)}
                                className="px-4 py-2 text-sm font-bold text-amber-700 hover:underline"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => {
                                  const comment = document.getElementById(`comment-${booking.booking_id}`).value;
                                  handleStatusUpdate(booking.booking_id, 'Pending', comment, 'suggest_change');
                                }}
                                disabled={actionLoading}
                                className="px-5 py-2 bg-amber-600 text-white rounded-xl text-sm font-bold disabled:opacity-50 hover:bg-amber-700 transition"
                              >
                                {actionLoading ? 'Sending…' : 'Send Suggestion'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {editingBooking && (
        <EditGuestBookingModal
          booking={editingBooking}
          services={services}
          saving={editLoading}
          onClose={() => setEditingBooking(null)}
          onSave={handleGuestBookingUpdate}
          showToast={showToast}
        />
      )}

      {selectedReceiptBooking && (
        <ReceiptModal booking={selectedReceiptBooking} onClose={() => setSelectedReceiptBooking(null)} />
      )}

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default ManagerBookingRequest;
